import { useEffect, useState } from "react";
import { cloneDeep, isEmpty } from "lodash";

// Types
import { KVP, SortDirection } from "../../../utils/types";

/**
 * Defines Pagination properties
 */
interface IPaginateable {
	page: number;
	limit: number;
}

/**
 * Defines Sortable properties
 */
interface ISortable {
	sortField: string;
	sortDirection: SortDirection;
}

interface ISearchable {
	searchField: string;
	searchString: string;
}

/**
 * Defines Searchable properties
 */
interface ISearchable {
	searchField: string;
	searchString: string;
}

/**
 * Defines callbacks for dynamic notifications
 */
interface INotifiable {
	callback: (cacheKey: string) => void;
}

/**
 * Defines base shape for Search Parameters
 * Extends: IPaginateable, ISortable
 */
export interface IParams extends IPaginateable, ISortable, ISearchable {
	model: KVP;
}

/**
 * Defines shape for parameter model validation, if needed
 */
interface IValidate {
	validate?: (model: KVP) => boolean;
}

/**
 * Defines shape for required search params on initialization
 * Extends: INotifiable, IValidate
 */
interface UseSearchParamsProps extends INotifiable, IValidate {
	params: IParams;
	/**
	 * Did we land on a page with existing params?
	 */
	existingParams?: URLSearchParams;
	shouldUpdateUrl: boolean;
	shouldShowDisabled: boolean;
}

/**
 * Define a set of static keys, search params will always have these
 */
export enum StaticKeys {
	Page = "page",
	Limit = "limit",
	SortField = "sortField",
	SortDir = "sortDir",
	SearchField = "searchField",
	SearchString = "searchString"
}

/**
 * Helper function to convert the cache key string into the `IParams` interface
 * This is mainly useful when making BFF calls, as the utility API function can simply
 * accept an object and map itself instead of devoting that logic to the page itself
 * @param localCacheKey The cacheKey object of the currently rendered `DataTable`
 * @returns IParams object with appropriate `StaticKeys` mapped
 */
export const getCacheKeyObject = (localCacheKey: string): IParams => {
	// Split cacheKey into individual parts
	let cacheKeyParts: Array<string> = localCacheKey.split("&");
	// Create mapping object
	let composedKey: IParams = {
		model: {},
		page: 0,
		limit: 0,
		sortField: "",
		sortDirection: SortDirection.Asc,
		searchField: "",
		searchString: ""
	};
	let cacheKeyKvp: KVP = {};
	// Process key parts
	cacheKeyParts.forEach((keySegment: string) => {
		// Split on equal
		const keySegmentParts: Array<string> = keySegment.split("=");
		// Assign to KVP
		cacheKeyKvp[keySegmentParts[0]] = keySegmentParts[1];
	});

	// For each StaticKey, map onto the IParams object
	Object.keys(StaticKeys).forEach((masterKey: string) => {
		switch (masterKey.toLowerCase()) {
			case StaticKeys.Limit.toLowerCase():
				if (cacheKeyKvp[StaticKeys.Limit]) {
					composedKey.limit = Number.parseInt(cacheKeyKvp[StaticKeys.Limit].toString());
				}
				break;
			case StaticKeys.Page.toLowerCase():
				if (cacheKeyKvp[StaticKeys.Page]) {
					composedKey.page = Number.parseInt(cacheKeyKvp[StaticKeys.Page].toString());
				}
				break;
			case StaticKeys.SearchField.toLowerCase():
				if (cacheKeyKvp[StaticKeys.SearchField]) {
					composedKey.searchField = cacheKeyKvp[StaticKeys.SearchField].toString();
				}
				break;
			case StaticKeys.SearchString.toLowerCase():
				if (cacheKeyKvp[StaticKeys.SearchString]) {
					composedKey.searchString = cacheKeyKvp[StaticKeys.SearchString].toString();
				}
				break;
			case StaticKeys.SortDir.toLowerCase():
				if (cacheKeyKvp[StaticKeys.SortDir]) {
					composedKey.sortDirection =
						cacheKeyKvp[StaticKeys.SortDir].toString().toLocaleLowerCase() === "asc"
							? SortDirection.Asc
							: SortDirection.Desc;
				}
				break;
			case StaticKeys.SortField.toLowerCase():
				if (cacheKeyKvp[StaticKeys.SortField]) {
					composedKey.sortField = cacheKeyKvp[StaticKeys.SortField].toString();
				}
				break;
		}
	});

	// Reconstruct the model object from searchField and searchString
	if (composedKey.searchField && composedKey.searchString) {
		const searchFields = composedKey.searchField.split(",");
		const searchValues = composedKey.searchString.split(",");

		searchFields.forEach((field: string, index: number) => {
			if (searchValues[index]) {
				composedKey.model[field] = searchValues[index];
			}
		});
	}

	return composedKey;
};

/**
 * Search Parameter Hook used for Data Tables.
 * Search Parameters will take data in the required shape, serialize the values into
 * a cacheKey, and use the cacheKey as a symbol of truth to provide for API Calls and generate
 * notifications to consumers. Also leverages provided validation on the given model if provided.
 * @param props typeof: UseSearchParamsProps
 */
const useClaimsSearchParams = (props: UseSearchParamsProps) => {
	// Deconstruct properties
	const { params, existingParams, shouldUpdateUrl, shouldShowDisabled, validate, callback } = props;
	// Deconstruct parameters
	const { page, limit, sortField, sortDirection, model } = params;

	// Handle internal state for instance
	const [currentPage, setCurrentPage] = useState<number>(page);
	const [currentLimit, setCurrentLimit] = useState<number>(limit);
	const [currentSortField, setCurrentSortField] = useState<string>(sortField);
	const [currentSortDirection, setCurrentSortDirection] = useState<SortDirection>(sortDirection);
	const [paramModel, setParamModel] = useState<KVP>(model);
	const [cacheKey, setCacheKey] = useState<string>("");
	/**
	 * We need to prevent updating some properties on the first pass due to
	 * React's lifecycle
	 */
	const [firstPass, setFirstPass] = useState<boolean>(true);

	/**
	 * Updates the cacheKey using the provided Model. Supports multi-field
	 * search functionality. CacheKey consists of certain static properties.
	 */
	const updateCacheKey = (): void => {
		// A necessary evil, URLSearchParams in Typescript is missing some properties in Javascript
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
		if (!firstPass || (existingParams as any).size === 0) {
			// Compose url query params
			const searchParams: Array<string> = [
				`page=${currentPage}`,
				`limit=${currentLimit}`,
				`sortField=${currentSortField}`,
				`sortDir=${currentSortDirection}`
			];

			// Compose model query params
			const customSearchFields: Array<string> = [];
			const customSearchValues: Array<string> = [];
			Object.keys(paramModel).forEach((key: string) => {
				// If the provided key has a value, append it
				if (!isEmpty(paramModel[key]?.toString().trim())) {
					customSearchFields.push(key);
					customSearchValues.push(paramModel[key].toString());
				}
			});
			searchParams.push(`searchField=${customSearchFields.join(",")}`);
			searchParams.push(`searchString=${customSearchValues.join(",")}`);

			// If this is a Disabled Table View, add the param to the Query String
			if (shouldShowDisabled) searchParams.push(`showDisabled=${shouldShowDisabled.toString()}`);

			// Set key
			setCacheKey(searchParams.join("&"));
		}
	};

	/**
	 * Returns a deep clone of the cacheKey for consumption purposes.
	 * Editing the provided cacheKey will not reflect on internal value.
	 * @returns cacheKey: string
	 */
	const getCacheKey = (): string => cloneDeep(cacheKey);

	/**
	 * Handles updating the search parameters in the URL. Does not trigger
	 * a page reload, updates window.history state.
	 * NOTE: Certain values are encoded, this is not a bug and does not affect network calls
	 * @param cacheKey Internal cacheKey
	 */
	const updateUrlSearchParams = (cacheKey: string): void => {
		if (shouldUpdateUrl && cacheKey !== "") {
			const url = new URL(window.location.href);
			const cacheKeyParts: Array<string> = cacheKey.split("&");

			// Clear previous unused search params
			if (cacheKeyParts.length > 0) {
				for (const key of url.searchParams.keys()) {
					if (cacheKeyParts.indexOf(key) === -1) url.searchParams.delete(key);
				}
			}

			// Serialize model into params
			cacheKeyParts.forEach((key: string) => {
				if (!isEmpty(key)) {
					const splitKey = key.split("=");
					const param: string = splitKey[0];
					const value: string = splitKey[1] ?? "";
					url.searchParams.set(param, value);
				}
			});

			// pushState vs replaceState:
			// pushState: Adds a new entry to the browser's history tab
			// replaceState: Updated the current entry
			// We should use replaceState here so the back button doesn't become a nightmare to use
			window.history.replaceState({}, "", decodeURIComponent(url.toString()));
		}
	};

	/**
	 * When static properties (Page, Limit, Sort Field, Sort Direction) are updated,
	 * trigger an update of the cacheKey
	 */
	useEffect(() => {
		updateCacheKey();
	}, [currentPage, currentLimit, currentSortField, currentSortDirection]);

	/**
	 * When the parameter model is updated, handles update of cacheKey
	 */
	useEffect(() => {
		// If validation was provided, validation must pass for the model in order to
		// update the cacheKey
		if (validate && typeof validate === "function" && validate(paramModel)) {
			updateCacheKey();
		}
		// If no validation was provided, update the cacheKey anyway
		else updateCacheKey();
	}, [paramModel]);

	/**
	 * Whenever the cacheKey is updated, update the URL Search Parameters
	 * and notify consumers of update
	 */
	useEffect(() => {
		updateUrlSearchParams(cacheKey);
		callback(cacheKey);
	}, [cacheKey]);

	/**
	 * This only needs to trigger if there are existing params and we're not on the first iteration
	 */
	useEffect(() => {
		// We only need to process this if there are existing params AND we're not on the first
		// lifecycle update
		if (existingParams && !firstPass) {
			// Deserialize these and integrate them into the current model
			const existingParamParts: Array<string> = existingParams.toString().split("&");
			const existingParamKvp: KVP = {};

			// Build a KVP of the existing search params
			existingParamParts.forEach((part: string) => {
				const { 0: partKey, 1: partValue } = part.split("=");
				existingParamKvp[partKey] = partValue;
			});
			// Iterate through the static keys
			Object.keys(StaticKeys).forEach((staticKey: string, index: number) => {
				// Determine if the existing param object has the static key
				const hasKey: boolean = Object.keys(existingParamKvp).indexOf(Object.values(StaticKeys)[index]) > -1;
				if (hasKey) {
					try {
						// The one time typescript pisses me off...
						switch ((StaticKeys as unknown as { [key: string]: string })[staticKey]) {
							case StaticKeys.Page:
								setCurrentPage(
									parseInt(existingParamKvp[(StaticKeys as { [key: string]: string })[staticKey]].toString())
								);
								break;
							case StaticKeys.Limit:
								setCurrentLimit(
									parseInt(existingParamKvp[(StaticKeys as { [key: string]: string })[staticKey]].toString())
								);
								break;
							case StaticKeys.SortDir:
								setCurrentSortDirection(
									existingParamKvp[(StaticKeys as { [key: string]: string })[staticKey]].toString().toLowerCase() ===
										"asc"
										? SortDirection.Asc
										: SortDirection.Desc
								);
								break;
							case StaticKeys.SortField:
								setCurrentSortField(existingParamKvp[(StaticKeys as { [key: string]: string })[staticKey]].toString());
								break;
							case StaticKeys.SearchField:
								processExistingSearchFields(existingParamKvp);
								break;
							// These are processed as part of the SearchFields, do nothing
							case StaticKeys.SearchString:
								break;
						}
					} catch (error: unknown) {
						console.error(
							`Tried to parse an existing search parameter with invalid value: ${(error as Error).message}`
						);
					}
				}
			});
		}
	}, [firstPass]);
	/**
	 * Some of the param strings are URL encoded, decode them as needed
	 * @param existingParamKvp The existing parameters passed in
	 * @returns Updated KVP with decoded params
	 */
	const decodeParamStrings = (existingParamKvp: KVP): KVP => {
		// Preprocessing
		if (existingParamKvp[StaticKeys.SearchField]) {
			existingParamKvp[StaticKeys.SearchField] = decodeURIComponent(
				existingParamKvp[StaticKeys.SearchField].toString()
			);
		}
		if (existingParamKvp[StaticKeys.SearchString]) {
			existingParamKvp[StaticKeys.SearchString] = decodeURIComponent(
				existingParamKvp[StaticKeys.SearchString].toString()
			);
		}
		return existingParamKvp;
	};

	/**
	 * Deserializes the existing parameters into the provided model
	 * If the provided model does not have similar properties, the outliers are ignored
	 * @param existingParamKvp The existing Parameters provided
	 */
	const processExistingSearchFields = (existingParamKvp: KVP): void => {
		// Perform some preprocessing
		existingParamKvp = decodeParamStrings(existingParamKvp);

		// Extract the array of strings
		if (existingParamKvp[StaticKeys.SearchField]) {
			const searchFields: Array<string> = existingParamKvp[StaticKeys.SearchField].toString().split(",");
			if (!isEmpty(searchFields)) {
				// Clone this so we don't overwrite props
				const localParamModel: KVP = cloneDeep(model);
				searchFields.forEach((key: string, index: number) => {
					// If the provided model has the key, update it
					if (Object.keys(localParamModel).indexOf(key) > -1 && existingParamKvp[StaticKeys.SearchString]) {
						// The provided value should exist at the same index as the searchString
						localParamModel[key] = existingParamKvp[StaticKeys.SearchString].toString().split(",")[index];
					}
				});
				setParamModel(localParamModel);
			}
		} else {
			const localParamModel: KVP = cloneDeep(model);
			setParamModel(localParamModel);
		}
	};

	/**
	 * A hacky approach. Each useEffect statement will process once on initial render. With pre-existing
	 * search parameters, this causes a problem where initial values are processed alongside pre-existing
	 * parameters. To avoid this, we only perform certain actions on first render and other actions on
	 * subsequent renders
	 */
	useEffect(() => {
		if (firstPass) setFirstPass(false);
	}, []);

	return {
		// Exposed properties
		currentPage,
		currentLimit,
		currentSortField,
		currentSortDirection,

		// Exposed React Dispatch delegates
		setCurrentPage,
		setCurrentLimit,
		setCurrentSortField,
		setCurrentSortDirection,
		setParamModel,

		// Exposed getters
		getCacheKey
	};
};

export default useClaimsSearchParams;
